import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  inject,
} from '@angular/core'
import { MatIconModule } from '@angular/material/icon'
import { TooltipDirective } from '../../directives/tooltip/tooltip.directive'

export type PillTheme =
  | 'success'
  | 'neutral'
  | 'danger'
  | 'primary'
  | 'true-primary'
  | 'lavender'
  | 'warning'
  | 'true-warning'
  | 'caution'
  | 'turquoise'
  | 'violet'
  | 'sky'
  | 'wisteria'
  | 'tag'

export type PillSize = 'large' | 'medium' | 'small'

export type PillShape = 'round' | 'square'

@Component({
  selector: 'wm-front-pills',
  templateUrl: './pills.component.html',
  styleUrls: ['./pills.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatIconModule, TooltipDirective],
})
export class PillsComponent {
  /** Text to display */
  public currentText: string

  /** Icon to display. The svg name must be used */
  public currentIcon?: string | 'bullet'

  /** Theme to display */
  public currentTheme?: PillTheme

  /** Size to display */
  public currentSize?: PillSize = 'medium'

  /** Shape to display */
  public currentShape?: PillShape = 'round'

  /** If the pill is transparent */
  public isTransparent? = false

  /** If the pill is reverted */
  public isReverted? = false

  protected _cdf = inject(ChangeDetectorRef)

  /** Will set the text at component level. It should already be translated */
  @Input()
  public set text(text: string) {
    this.currentText = text
    this.detectChange()
  }

  /** Will set the text at component level */
  @Input()
  public set icon(icon: string | 'bullet' | undefined) {
    this.currentIcon = icon
    this.detectChange()
  }

  /** Will set the theme at component level */
  @Input()
  public set theme(theme: PillTheme) {
    this.currentTheme = theme
    this.detectChange()
  }

  /** Will set the theme */
  @Input()
  public set size(size: PillSize) {
    this.currentSize = size
    this.detectChange()
  }

  /** Will set the shape */
  @Input()
  public set shape(shape: PillShape) {
    this.currentShape = shape
    this.detectChange()
  }

  @Input()
  public set transparent(transparent: boolean) {
    this.isTransparent = transparent
    this.detectChange()
  }

  @Input()
  public set reverted(reverted: boolean) {
    this.isReverted = reverted
    this.detectChange()
  }

  /** Will trigger the change detector to fire */
  private detectChange(): void {
    this._cdf.markForCheck()
  }
}
